// ── ExportSummaryPopup — Post-export summary modal ─────────────────────────
import React from 'react';
import { X, Package } from 'lucide-react';
import { useEscapeLayer } from '@/shared/hooks/useEscapeLayer';
import type { ExportSummary } from '../types';

import { fmtBytes as formatBytes } from '@/utils/format';

interface ExportSummaryPopupProps {
  summary: ExportSummary;
  onDismiss: () => void;
  onNewExport: () => void;
}

export function ExportSummaryPopup({
  summary,
  onDismiss,
  onNewExport,
}: ExportSummaryPopupProps) {
  useEscapeLayer(onDismiss);

  return (
    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-20 flex items-center justify-center p-4">
      <div className="pac-glass-raised rounded-lg p-5 max-w-lg w-full space-y-4 shadow-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-neutral-100">
            <Package className="w-4 h-4 text-emerald-400" />
            Export Summary
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="text-neutral-500 hover:text-neutral-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="pac-glass rounded px-3 py-2">
            <div className="text-[9px] text-neutral-600 uppercase tracking-wider">Scopes</div>
            <div className="text-lg text-neutral-100 font-semibold tabular-nums">{summary.scopeCount}</div>
          </div>
          <div className="pac-glass rounded px-3 py-2">
            <div className="text-[9px] text-neutral-600 uppercase tracking-wider">Namespaces</div>
            <div className="text-lg text-neutral-100 font-semibold tabular-nums">{summary.namespaceCount}</div>
          </div>
          <div className="pac-glass rounded px-3 py-2">
            <div className="text-[9px] text-neutral-600 uppercase tracking-wider">Outcomes</div>
            <div className="text-lg text-neutral-100 font-semibold tabular-nums">{summary.outcomeCount}</div>
          </div>
          <div className="pac-glass rounded px-3 py-2">
            <div className="text-[9px] text-neutral-600 uppercase tracking-wider">Handlers</div>
            <div className="text-lg text-neutral-100 font-semibold tabular-nums">{summary.handlerCount}</div>
          </div>
          <div className="pac-glass rounded px-3 py-2">
            <div className="text-[9px] text-neutral-600 uppercase tracking-wider">Hot Stream</div>
            <div className="text-lg text-neutral-100 font-semibold tabular-nums">{summary.hotStreamCount}</div>
          </div>
          <div className="pac-glass rounded px-3 py-2">
            <div className="text-[9px] text-neutral-600 uppercase tracking-wider">Archive Files</div>
            <div className="text-lg text-neutral-100 font-semibold tabular-nums">{summary.archiveFileCount}</div>
          </div>
          {summary.artifactFileCount > 0 && (
            <div className="pac-glass rounded px-3 py-2">
              <div className="text-[9px] text-sky-700 uppercase tracking-wider">GCS Artifacts</div>
              <div className="text-lg text-sky-300 font-semibold tabular-nums">{summary.artifactFileCount}</div>
              {summary.artifactTotalBytes > 0 && (
                <div className="text-[9px] text-neutral-500 tabular-nums">
                  {formatBytes(summary.artifactTotalBytes)}
                </div>
              )}
            </div>
          )}
        </div>

        {summary.namespaces.length > 0 && (
          <div>
            <div className="text-[10px] text-neutral-500 mb-1">Namespaces</div>
            <div className="flex flex-wrap gap-1">
              {summary.namespaces.map((ns: string) => (
                <span key={ns} className="pac-glass-input text-neutral-300 rounded px-1.5 py-0.5 text-[10px] font-mono">
                  {ns}
                </span>
              ))}
            </div>
          </div>
        )}

        {summary.handlers.length > 0 && (
          <div>
            <div className="text-[10px] text-neutral-500 mb-1">
              Handlers{summary.handlerCount > 12 ? ` (showing 12 of ${summary.handlerCount})` : ''}
            </div>
            <div className="flex flex-wrap gap-1">
              {summary.handlers.map((h: string) => (
                <span key={h} className="bg-sky-950/60 text-sky-300 rounded px-1.5 py-0.5 text-[10px] font-mono">
                  {h}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1 border-t border-neutral-800">
          <button
            className="px-3 py-1.5 rounded border border-neutral-700 text-xs text-neutral-300 hover:border-neutral-500"
            onClick={onDismiss}
          >
            Dismiss
          </button>
          <button
            className="px-3 py-1.5 rounded border border-emerald-700 text-xs text-emerald-200 hover:border-emerald-500 flex items-center gap-1.5"
            onClick={onNewExport}
          >
            Start New Export
          </button>
        </div>
      </div>
    </div>
  );
}
